import { z } from 'zod';

// ==================== Editor form schema (RHF + Zod) ====================
// The form mirrors the create/update payloads (F3.x). Amounts are kept as
// STRINGS end-to-end — the server does the authoritative decimal math (F3.3);
// the client only display-sums for a labelled estimate (previewTotals below).

/** A non-negative money string with up to 2 decimals, e.g. "0", "12", "12.5", "1250.00". */
const MONEY_RE = /^\d+(\.\d{1,2})?$/;

const moneyString = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .regex(MONEY_RE, `${label} must be a number like 1250.00`);

/** Optional money string — empty allowed (defaults to "0" on submit). */
const optionalMoneyString = z
  .string()
  .trim()
  .refine(v => v === '' || MONEY_RE.test(v), {
    message: 'Must be a number like 1250.00',
  });

export const lineItemSchema = z.object({
  description: z.string().trim().min(1, 'Description is required'),
  amount: moneyString('Amount'),
});

/**
 * Editor schema. `valid_until` is always omittable (F3.4 — quotes may omit
 * validity; invoices ignore it). `client_id` is required — the inline-create
 * dialog assigns one before submit. `type` is only meaningful on create.
 */
export const documentFormSchema = z.object({
  type: z.enum(['invoice', 'quote']),
  client_id: z.string().min(1, 'Select or create a client'),
  contact: z.string().trim().max(255).optional().or(z.literal('')),
  event: z.string().trim().max(255).optional().or(z.literal('')),
  prepared_label: z.string().trim().max(255).optional().or(z.literal('')),
  terms: z.string().trim().optional().or(z.literal('')),
  accept_line: z.string().trim().optional().or(z.literal('')),
  issue_date: z.string().optional().or(z.literal('')),
  due_date: z.string().optional().or(z.literal('')),
  valid_until: z.string().optional().or(z.literal('')),
  discount: optionalMoneyString,
  line_items: z.array(lineItemSchema).min(1, 'Add at least one line item'),
});

export type DocumentFormValues = z.infer<typeof documentFormSchema>;

export const emptyLineItem = (): { description: string; amount: string } => ({
  description: '',
  amount: '',
});

export const blankDocumentForm = (
  type: 'invoice' | 'quote' = 'invoice'
): DocumentFormValues => ({
  type,
  client_id: '',
  contact: '',
  event: '',
  prepared_label: '',
  terms: '',
  accept_line: '',
  issue_date: '',
  due_date: '',
  valid_until: '',
  discount: '',
  line_items: [emptyLineItem()],
});
